import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, UserCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const positions = ['GK', 'DEF', 'MID', 'FWD'];

export default function PlayersPage() {
  const { token, organization } = useAuth();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [filterTeam, setFilterTeam] = useState('all');
  const [uploading, setUploading] = useState(false);
  const excelInputRef = React.useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    position: 'MID',
    team_id: '',
    date_of_birth: ''
  });

  useEffect(() => {
    if (organization) {
      fetchTeams();
      fetchPlayers();
    }
  }, [organization]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/players/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPlayer) {
        const response = await fetch(`${API_URL}/api/players/${editingPlayer.player_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            number: formData.number ? parseInt(formData.number) : 0,
            position: formData.position
          })
        });
        if (!response.ok) throw new Error('Failed to update player');
        toast.success('Player updated!');
      } else {
        const response = await fetch(`${API_URL}/api/players`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            number: formData.number ? parseInt(formData.number) : 0,
            position: formData.position,
            team_id: formData.team_id,
            org_id: organization.org_id,
            date_of_birth: formData.date_of_birth || null
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to create player');
        }
        toast.success('Player created!');
      }
      
      setDialogOpen(false);
      setEditingPlayer(null);
      setFormData({ name: '', number: '', position: 'MID', team_id: '', date_of_birth: '' });
      fetchPlayers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (playerId) => {
    if (!confirm('Are you sure you want to delete this player?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/players/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete player');
      toast.success('Player deleted!');
      fetchPlayers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      number: player.number.toString(),
      position: player.position,
      team_id: player.team_id,
      date_of_birth: player.date_of_birth || ''
    });
    setDialogOpen(true);
  };

  const teamMap = teams.reduce((acc, team) => {
    acc[team.team_id] = team.name;
    return acc;
  }, {});

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const targetTeamId = filterTeam !== 'all' ? filterTeam : null;
    if (!targetTeamId) {
      toast.error('Please select a team first, then upload the Excel file');
      excelInputRef.current.value = '';
      return;
    }
    
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/players/bulk-upload/${targetTeamId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: await file.arrayBuffer()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPlayers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      excelInputRef.current.value = '';
    }
  };

  const filteredPlayers = filterTeam === 'all' 
    ? players 
    : players.filter(p => p.team_id === filterTeam);

  return (
    <DashboardLayout>
      <div data-testid="players-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
              Players
            </h1>
            <p className="text-gray-600 mt-2">Manage player roster ({filteredPlayers.length}{filterTeam !== 'all' ? ` of ${players.length}` : ''} players)</p>
          </div>
          <div className="flex gap-3">
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger className="w-48 bg-white border-gray-200 text-black">
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all" className="text-black hover:bg-gray-100">All Teams</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.team_id} value={team.team_id} className="text-black hover:bg-gray-100">
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="file"
              ref={excelInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleExcelUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => excelInputRef.current?.click()}
              disabled={uploading || filterTeam === 'all'}
              className="border-gray-200 text-gray-700 hover:text-black"
              data-testid="upload-excel-btn"
              title={filterTeam === 'all' ? 'Select a team first' : 'Upload Excel (Name, Number, Position, DOB)'}
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Uploading...' : 'Upload Excel'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-black btn-lift"
                  data-testid="add-player-btn"
                  onClick={() => {
                    setEditingPlayer(null);
                    setFormData({ name: '', number: '', position: 'MID', team_id: teams[0]?.team_id || '' });
                  }}
                >
                  <Plus size={18} className="mr-2" />
                  Add Player
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 text-black">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">
                    {editingPlayer ? 'Edit Player' : 'Add New Player'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-700">Player Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., John Doe"
                      required
                      data-testid="player-name-input"
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">Jersey Number</Label>
                      <Input
                        type="number"
                        value={formData.number}
                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                        placeholder="e.g., 10"
                        min="1"
                        max="99"
                        data-testid="player-number-input"
                        className="mt-2 bg-gray-100 border-gray-200 text-black"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Position</Label>
                      <Select 
                        value={formData.position} 
                        onValueChange={(value) => setFormData({ ...formData, position: value })}
                      >
                        <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {positions.map((pos) => (
                            <SelectItem key={pos} value={pos} className="text-black hover:bg-gray-100">
                              {pos}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {!editingPlayer && (
                    <div>
                      <Label className="text-gray-700">Team</Label>
                      <Select 
                        value={formData.team_id} 
                        onValueChange={(value) => setFormData({ ...formData, team_id: value })}
                      >
                        <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {teams.map((team) => (
                            <SelectItem key={team.team_id} value={team.team_id} className="text-black hover:bg-gray-100">
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Date of Birth (Optional)</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                      data-testid="player-dob-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 border-gray-200 text-black hover:bg-gray-100"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                      data-testid="player-submit-btn"
                    >
                      {editingPlayer ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Players Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredPlayers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <UserCircle className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-black text-lg font-medium mb-2">No players yet</h3>
            <p className="text-gray-500">Add players to your teams</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-600 uppercase text-xs font-bold">#</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold">Player</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold">Team</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold">Position</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">Goals</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">Assists</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">Cards</TableHead>
                  <TableHead className="text-gray-600 uppercase text-xs font-bold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlayers.map((player) => (
                  <TableRow 
                    key={player.player_id} 
                    className="border-gray-100 table-row-hover"
                    data-testid={`player-row-${player.player_id}`}
                  >
                    <TableCell className="font-mono text-emerald-500 font-bold">
                      {player.number}
                    </TableCell>
                    <TableCell className="text-black font-medium">{player.name}</TableCell>
                    <TableCell className="text-gray-600">{teamMap[player.team_id] || 'Unknown'}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                        {player.position}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-black">{player.goals}</TableCell>
                    <TableCell className="text-center text-black">{player.assists}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-yellow-500">{player.yellow_cards}</span>
                      <span className="text-gray-600 mx-1">/</span>
                      <span className="text-red-500">{player.red_cards}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(player)}
                          className="text-gray-600 hover:text-black"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(player.player_id)}
                          className="text-gray-600 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
